
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Heart, Trash2, Settings, Eye } from 'lucide-react';
import { mockTerraces } from '@/data/mockTerraces';
import useAuth from '@/hooks/useAuth';
import SunStatusIndicator from '@/components/SunStatusIndicator';
import { toast } from '@/hooks/use-toast';
import { DashboardCard } from './widgets';

const FavoritesTab = () => {
  const { user, profile } = useAuth();
  
  if (!user || !profile) return null;
  
  // Find terraces that match the user's favorites
  const favoriteTerraces = mockTerraces.filter(terrace => 
    profile.favorites?.includes(terrace.id)
  );
  
  const handleRemoveFavorite = (terraceId: string) => {
    // In a real app, this would update the database
    toast({
      title: "Removed from favorites",
      description: "Terrace removed from your favorites list."
    });
  };

  return (
    <div className="space-y-6">
      {/* Enhanced Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-coral-pink/10 rounded-lg">
            <Heart className="h-6 w-6 text-coral-pink" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Favorite Terraces</h1>
            <p className="text-muted-foreground">
              Manage your saved terraces and get notifications
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Badge variant="secondary" className="bg-coral-pink/10 text-coral-pink border-coral-pink/30">
            {profile.favorites?.length || 0} favorites
          </Badge>
          <Button variant="outline" size="sm">
            <Settings className="h-4 w-4 mr-1" />
            Manage
          </Button>
        </div>
      </div>
      
      {(!profile.favorites || profile.favorites.length === 0) ? (
        <DashboardCard
          title="No Favorites Yet"
          description="Start building your collection of favorite terraces"
          icon={<Heart className="h-5 w-5" />}
          variant="default"
        >
          <div className="text-center py-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-coral-pink/10 to-coral-pink/5 mb-4">
              <Heart className="h-8 w-8 text-coral-pink" />
            </div>
            <h3 className="text-lg font-medium mb-2">Discover Your Perfect Spots</h3>
            <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
              Explore Stockholm's best terraces and save your favorites for quick access and notifications.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild>
                <Link to="/list">
                  <Eye className="h-4 w-4 mr-2" />
                  Browse Terraces
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/">
                  <Heart className="h-4 w-4 mr-2" />
                  View Map
                </Link>
              </Button>
            </div>
          </div>
        </DashboardCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {favoriteTerraces.map(terrace => (
            <Card key={terrace.id} className="group overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-coral-pink/10 border-coral-pink/20">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={terrace.photo_url || 'https://images.unsplash.com/photo-1500673922987-e212871fec22'}
                  alt={terrace.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute top-3 right-3">
                  <Button 
                    variant="outline"
                    size="sm"
                    className="bg-white/95 hover:bg-white border-white/20 shadow-lg"
                    onClick={() => handleRemoveFavorite(terrace.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
                <div className="absolute top-3 left-3">
                  <SunStatusIndicator status={terrace.sunStatus} />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent p-4">
                  <div className="text-white">
                    <div className="text-sm opacity-90">{terrace.type}</div>
                  </div>
                </div>
              </div>
              <CardContent className="p-4">
                <h3 className="text-lg font-semibold mb-2 group-hover:text-coral-pink transition-colors">
                  {terrace.name}
                </h3>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {terrace.address}
                </p>
                <div className="flex gap-2">
                  <Button asChild variant="outline" className="flex-1">
                    <Link to={`/terrace/${terrace.slug}`}>
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Link>
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="px-3 text-coral-pink hover:text-coral-pink hover:bg-coral-pink/10"
                  >
                    <Heart className="h-4 w-4 fill-current" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default FavoritesTab;
